import { isUserAuthenticated } from "@/middleware/auth";
import {
  getChatFromLocalDB,
  addPinnedMessageIdToChatInLocalDB,
  removePinnedMessageIdFromChatInLocalDB,
} from "@/indexedDB/services/chats";

import { SocketEventActionType, MessageEventType } from "./types";

import { updateMessageByServerIdInLocalDB } from "@/indexedDB/services/messages";
import { removePinnedMessageByServerIdFromLocalDB } from "@/indexedDB/services/pinnedMessages";
import { ensurePinnedMessageLocal } from "@/middleware/pinnedReconcile";

export const handlePinnedMessageEvent = async (data: MessageEventType) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  switch (data.action) {
    case SocketEventActionType.CREATE:
      await onPinMessage({ eventPayload: data });
      break;
    case SocketEventActionType.DELETE:
      await onUnpinMessage({ eventPayload: data });
      break;
    default:
      break;
  }
};

const onPinMessage = async ({
  eventPayload,
}: {
  eventPayload: MessageEventType;
}) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }
  const chatDetails = await getChatFromLocalDB(eventPayload.message.chat_id);
  if (!chatDetails) {
    return;
  }

  // Keep `chat.pinnedMessageIds` (the single source of truth the new-sync
  // reconcile enforces) authoritative FIRST. Without this, a concurrent
  // fire-and-forget reconcile would read the stale chat field — which lacks the
  // just-pinned id — and PRUNE the message right back out (re-introducing the
  // disappear bug as a live race). Do it before materializing the body so any
  // reconcile that races sees the new id.
  await addPinnedMessageIdToChatInLocalDB(
    chatDetails.id,
    eventPayload.message.id,
  );

  // Materialize the pinned body via the SHARED helper: if the message isn't in
  // the local `messages` table (e.g. a live pin of unloaded history), this
  // fetches + decrypts it by id and stores it in BOTH tables. Previously this
  // path returned silently on a local miss, so live-pinning an unloaded message
  // left the pinned panel empty until a full reload.
  await ensurePinnedMessageLocal({
    chatId: chatDetails.id,
    eCSK: chatDetails.eCSK,
    messageId: eventPayload.message.id,
  });
};

const onUnpinMessage = async ({
  eventPayload,
}: {
  eventPayload: MessageEventType;
}) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }
  const chatDetails = await getChatFromLocalDB(eventPayload.message.chat_id);
  if (!chatDetails) {
    return;
  }

  // Keep `chat.pinnedMessageIds` authoritative: drop the unpinned id so the
  // next reconcile doesn't treat it as still-pinned and re-fetch/re-add it.
  await removePinnedMessageIdFromChatInLocalDB(
    chatDetails.id,
    eventPayload.message.id,
  );

  // Remove the `pinnedMessages` row REGARDLESS of whether the canonical
  // `messages` row is local. Previously this early-returned on a `messages`
  // miss, leaving a stale row in the pinned panel when the body had never been
  // loaded locally. `pinnedMessages` is keyed on the server id, so delete by it
  // directly.
  await removePinnedMessageByServerIdFromLocalDB(eventPayload.message.id);

  // Best-effort: clear the pinned flag on the canonical row if it exists.
  // `updateMessageByServerIdInLocalDB` is a no-op when the row is missing.
  await updateMessageByServerIdInLocalDB(eventPayload.message.id, {
    isPinned: false,
  });
};
